/*
 * @Author:
 * @Date: 2023-12-14 13:32:45
 * @LastEditors: Merlin
 * @LastEditTime: 2025-05-15 10:15:55
 * @Description:
 */
import { defineStore } from 'pinia';
import useSettingsStore from './settings/index';
import { useMailSocket } from '@/socket/mail/index'; 
import {useJoinStateStore} from '@/stores/cansino/joinState';

// ↓ store 中的某币种的信息
interface OneCurrency {
    balance: number; // 余额（实时）
    balanceDelay: number; // 余额（在“动画结束后” 才更新）
    bonusBalance: number;
    combatBalance: number;
}

// ↓ 接口返回的某币种的信息
interface OneBalanceInfo {
    balance: number; // 余额
    cashback: number;
    rakeBackValue: number;
    rakeBackValueUSD: number;
}

// ↓ 余额变化的一个列队成员
interface QueueMember {
    timeStamp: number; //启动时间
    old: {
        // 余额变化前
        currencyName: string;
        balance: number;
    };
    new: {
        // 余额变化后
        currencyName: string;
        balance: number;
    };
}

interface FreezItem {
    coin_cnt: string;
    coin_type: string;
    game: string;
    game_id: number;
}
interface UserInfo {
    name?: string;
    email?: string;
    avatar?: string;
    isVip?: string;
    lvl?: number;
    id: number;
    _id: number;
    lang?: string; // 设定的界面语言
    currentCurrency?: string; // 当前币种
    balanceQueue?: Array<QueueMember>;
    two_factor_enabled?: boolean;
    currencies?: {
        // 余额信息
        corgi?: OneCurrency;
        btc?: OneCurrency;
        cny?: OneCurrency;
        eth?: OneCurrency;
        usdt?: OneCurrency;
    };
    unread: {
        system: number;
        person: number;
    };
    freezes: FreezItem[]; // 冻结余额
}

interface BalaceWsGiven {
    btc?: OneBalanceInfo;
    cny?: OneBalanceInfo;
    eth?: OneBalanceInfo;
    usdt?: OneBalanceInfo;
}

export const useUserStore = defineStore('user', {
    state(): UserInfo {
        // 👇保存用户相关信息
        return {
            name: '', // 用户名
            lvl: 1, // 疑似是用户等级
            id: 0, // 用户 id（容错措施：使调用 ._id 和 .id 都有效）
            _id: 0, // 用户 id（接口返回值）
            avatar: '', // 用户头像
            currencies: {}, // 用户余额
            currentCurrency: '', // 当前币种
            email: '',
            isVip: '',
            lang: '',
            balanceQueue: [], // 等待余额变化的列队
            two_factor_enabled: false,
            unread: { // 未读消息数量 
                system: 0,
                person: 0,
            },
            freezes: [],
        };
    },
    getters: {
        // 👇显示“当前币种” 的名称，余额，等
        oNowCurrency(oState) {
            const { pickedCurrency } = useSettingsStore();
            const { balance = 0, balanceDelay = 0 } = oState.currencies[pickedCurrency] || {};
            const oResult = {
                currencyName: pickedCurrency, // 当前币种名称
                balance,
                balanceDelay,
            };
            // console.log("当前币种信息更新：", deepCopy(oResult));
            return oResult;
        },
        // 👇 查询登录状态
        isLoggedIn(oState) {
            // 接口返回用户余额了，说明已经登录（此方式仍有优化空间）
            const aCurrencyArr = Object.keys(oState.currencies);
            return aCurrencyArr.length > 0;
            /* 
                最后的说明 💯💯💯  
                此处返回值在用户点击之后取值尚可，可以取到相对准确的值，因为这时“大概率” 接口已经查询完毕，在此可取得准确的值
                如果在页面加载之初到立即在此取值，有大风险取不到正确值，原因同上
                推荐以下方法查询登录状态：
                const oUser = await getUserInfo();
                console.log('已经登录?', oUser.hasLogged);
                或者：
                const {hasLogged} = await getUserInfo();
                console.log('已经登录?', hasLogged);
            */
        },
    },
    actions: {
        setUserInfo(oUserInfo: UserInfo) {
            Object.keys(this).forEach((sKey) => {
                if (sKey === 'freezes' && this.freezes.length > 0) return;
                if (Reflect.has(oUserInfo, sKey)) {
                    this[sKey] = oUserInfo[sKey];
                }
            });
            if (oUserInfo._id > 0) {
                this.id = oUserInfo._id;
            }
        },
        // 👇 更新余额信息
        updataOneBalance(oBalance: BalaceWsGiven) {
            // 入参 oBalance 通常只有一个键值对，格式示例如下： 
            // {usdt: {balance: 6276.72001438, cashback: 0, rakeBackValue: 0, rakeBackValueUSD: 0}} 
            Object.entries(oBalance).forEach((cur) => {
                const [currencyName, oCurrencyInfo] = cur;
                const { balance, freezes } = oCurrencyInfo as any;
                if (Array.isArray(freezes)) {
                    this.freezes = freezes;
                }
                this.currencies[currencyName] ||= {};
                this.currencies[currencyName].balance = balance;
                // ↑ 实时余额，可以直接赋值
                // ↓ 延迟的余额，判断后赋值
                if (Reflect.has(this.currencies[currencyName], 'balanceDelay') === false) {
                    this.currencies[currencyName].balanceDelay = balance; // 初始赋值
                }
                if (!this.balanceQueue.length) {
                    // 未启用列队（可直接更新延迟的余额）
                    this.currencies[currencyName].balanceDelay = balance;
                    return;
                }
                // ↓ 已经启动了列队，找到目标项，记录金额变化后的值 
                for (const oThisQ of this.balanceQueue) {
                    if (oThisQ.old.currencyName === currencyName && !oThisQ.new) {
                        oThisQ.new = { currencyName, ...oCurrencyInfo };
                        return;
                    }
                }
            });
        },
        // ↓ 启动列队（列队延长）
        queueStart(
            // ↓ 默认 true，检查上局游戏历史记录，找到了就插入。目前 plinko 有传入 false 
            checkLastRoundHistory=true,
        ) {
            if (checkLastRoundHistory) {
                try{
                    // ↓ 尝试插入上局扑空的数据 
                    useJoinStateStore().showDateInQueue('', true);
                }catch(err){
                    console.warn('queueStart() err', err);
                }
            }
            const timeStamp = Date.now();
            this.balanceQueue.push({
                timeStamp,
                old: { ...this.oNowCurrency },
                new: null, // ← 在当前页搜索 updataOneBalance 它将为 new 赋值 
            });
            // console.log(this.balanceQueue, 'this.balanceQueue-queueStart')
            return timeStamp;
        },
        // ↓ 更新列队（列队变短）
        // ↓ 通常在游戏动画播放完毕后执行
        queueForward(timeStamp?: number) {
            // console.log(this.balanceQueue, 'this.balanceQueue-queueForward')
            const index = this.balanceQueue.findIndex((cur) => {
                // ↓ 按道理这里早就有值了，早就按新消息为 new 赋值了 
                const updated = !!cur.new;
                if (timeStamp > 0) {
                    return updated && cur.timeStamp === timeStamp;
                }
                return updated; // 取首个
            });
            if (index === -1) {
                console.warn(`queueForward() 注意，找不到目标 search ${timeStamp} in ↓\n`);
                console.log(deepCopy(this.balanceQueue));
                return;
            }
            const [oTarget] = this.balanceQueue.splice(index, 1); // 使命完成了，挖走
            // console.log(oTarget, 'oTarget-queueForward')
            const { currencyName, balance } = oTarget.new;
            // console.log(balance, 'balance-queueForward')
            // ↓ 更新延迟的余额
            this.currencies[currencyName].balanceDelay = balance;
        },
        // ↓ 删除队列
        // ↓ 用于启动队列后，请求报错需要删除队列 
        queueDelete(timeStamp:number){
            const index = this.balanceQueue.findIndex((cur) => {
                return cur.timeStamp === timeStamp;
            });
            if (index === -1) return console.warn('queueDelete() 注意，找不到目标');
            this.balanceQueue.splice(index, 1);
        },
        async updateUnreadCount(){
            const { EMIT_MAIL_COUNT } = useMailSocket();
            const res = await EMIT_MAIL_COUNT();
            if (!res || !res.response) {
                return console.error('查询未读消息数量失败'); 
            }
            // console.log('未读消息数量：', deepCopy(res.response));
            this.unread = {
                system: res.response?.systemCount || 0,
                person: res.response?.personCount || 0,
            };
        },
    },
});

